import { useMemo, useState } from "react";
import type { EvalAnnotation, FeedbackTraceResponse } from "../../types";
import type { GoldenRow, GoldenRowExpectedCitation } from "../../types/goldenRow";
import {
  canExport,
  canShowGoldenRowPreview,
} from "../../utils/annotationTransitions";
import { buildGoldenRow } from "../../utils/buildGoldenRow";
import { formatGoldenNdjson } from "../../utils/formatGoldenNdjson";
import {
  formatGoldenRowKeywords,
  parseGoldenRowKeywords,
} from "../../utils/parseGoldenRowKeywords";
import styles from "./GoldenRowPreview.module.css";

export interface GoldenRowPreviewProps {
  trace: FeedbackTraceResponse;
  annotation?: EvalAnnotation;
  reviewerIdentity: string;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onMarkExported: (input: { exportedToGoldenId: string }) => void;
}

function buildEffectiveRow(row: GoldenRow, keywordsInput: string): GoldenRow {
  return {
    ...row,
    expected_answer_keywords: parseGoldenRowKeywords(keywordsInput),
  };
}

function updateCitationAtIndex(
  citations: GoldenRowExpectedCitation[],
  index: number,
  patch: Partial<GoldenRowExpectedCitation>,
): GoldenRowExpectedCitation[] {
  return citations.map((citation, citationIndex) =>
    citationIndex === index ? { ...citation, ...patch } : citation,
  );
}

export function GoldenRowPreview({
  trace,
  annotation,
  reviewerIdentity,
  isSubmitting = false,
  errorMessage,
  onMarkExported,
}: GoldenRowPreviewProps) {
  const defaults = useMemo(
    () => buildGoldenRow(trace, annotation, reviewerIdentity),
    [trace, annotation, reviewerIdentity],
  );
  const [row, setRow] = useState<GoldenRow>(defaults);
  const [keywordsInput, setKeywordsInput] = useState(() =>
    formatGoldenRowKeywords(defaults.expected_answer_keywords),
  );
  const [isCopied, setIsCopied] = useState(false);

  const effectiveRow = useMemo(
    () => buildEffectiveRow(row, keywordsInput),
    [row, keywordsInput],
  );

  const showPreview = canShowGoldenRowPreview(
    annotation?.status,
    annotation?.sanitised,
  );

  if (!showPreview) {
    return null;
  }

  const ndjsonLine = formatGoldenNdjson(effectiveRow);
  const isGoldenIdPlaceholder = row.id.includes("<number>");
  const isExported =
    annotation?.exported === true ||
    annotation?.status === "exported" ||
    Boolean(annotation?.exported_to_golden_id);
  const canRecordExport = canExport(
    annotation?.status,
    annotation?.sanitised,
    annotation?.exported,
  );

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(ndjsonLine);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy golden row:", error);
    }
  }

  function handleSubmitExport(event: React.FormEvent) {
    event.preventDefault();
    onMarkExported({ exportedToGoldenId: row.id.trim() });
  }

  if (isExported) {
    return (
      <section
        className={styles.panel}
        data-testid="golden-row-exported"
      >
        <h3 className={styles.title}>Golden row exported</h3>
        <p className={styles.success}>
          Export recorded as{" "}
          <strong>{annotation?.exported_to_golden_id ?? row.id}</strong>.
        </p>
        <pre className={styles.preview} aria-label="Exported NDJSON line">
          {ndjsonLine}
        </pre>
      </section>
    );
  }

  return (
    <section className={styles.panel} data-testid="golden-row-preview">
      <h3 className={styles.title}>Golden row export</h3>
      <p className={styles.hint}>
        Edit fields as needed, copy the NDJSON line, then record export with
        the golden row id.
      </p>

      <div className={styles.grid}>
        <label className={styles.field}>
          <span className={styles.label}>Golden row id</span>
          <input
            type="text"
            className={styles.input}
            value={row.id}
            onChange={(event) =>
              setRow((current) => ({ ...current, id: event.target.value }))
            }
            aria-label="Golden row id"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Authored at</span>
          <input
            type="date"
            className={styles.input}
            value={row.authored_at}
            onChange={(event) =>
              setRow((current) => ({
                ...current,
                authored_at: event.target.value,
              }))
            }
            aria-label="Authored at"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Author</span>
          <input
            type="text"
            className={styles.input}
            value={row.author}
            onChange={(event) =>
              setRow((current) => ({ ...current, author: event.target.value }))
            }
            aria-label="Author"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Jurisdiction</span>
          <input
            type="text"
            className={styles.input}
            value={row.jurisdiction}
            onChange={(event) =>
              setRow((current) => ({
                ...current,
                jurisdiction: event.target.value,
              }))
            }
            aria-label="Jurisdiction"
          />
        </label>
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Question</span>
        <textarea
          className={styles.textarea}
          value={row.question}
          onChange={(event) =>
            setRow((current) => ({ ...current, question: event.target.value }))
          }
          rows={3}
          aria-label="Question"
        />
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Expected answer keywords</span>
        <input
          type="text"
          className={styles.input}
          value={keywordsInput}
          onChange={(event) => setKeywordsInput(event.target.value)}
          placeholder="Comma-separated keywords"
          aria-label="Expected answer keywords"
        />
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Expected clarification</span>
        <input
          type="text"
          className={styles.input}
          value={row.expected_clarification ?? ""}
          onChange={(event) =>
            setRow((current) => ({
              ...current,
              expected_clarification: event.target.value.trim() || null,
            }))
          }
          placeholder="Leave empty for null"
          aria-label="Expected clarification"
        />
      </label>

      <div className={styles.grid}>
        <label className={styles.field}>
          <span className={styles.label}>Min retrieval precision</span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className={styles.numberInput}
            value={row.min_retrieval_precision}
            onChange={(event) => {
              const parsed = Number.parseFloat(event.target.value);
              setRow((current) => ({
                ...current,
                min_retrieval_precision: Number.isFinite(parsed) ? parsed : 0,
              }));
            }}
            aria-label="Min retrieval precision"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Min retrieval recall</span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className={styles.numberInput}
            value={row.min_retrieval_recall}
            onChange={(event) => {
              const parsed = Number.parseFloat(event.target.value);
              setRow((current) => ({
                ...current,
                min_retrieval_recall: Number.isFinite(parsed) ? parsed : 0,
              }));
            }}
            aria-label="Min retrieval recall"
          />
        </label>
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Notes</span>
        <textarea
          className={styles.textarea}
          value={row.notes ?? ""}
          onChange={(event) =>
            setRow((current) => ({
              ...current,
              notes: event.target.value.trim() || null,
            }))
          }
          rows={3}
          aria-label="Notes"
        />
      </label>

      {row.expected_citations.length > 0 && (
        <div className={styles.field}>
          <span className={styles.label}>Expected citations</span>
          <ul className={styles.citationList}>
            {row.expected_citations.map((citation, index) => (
              <li
                key={`${citation.doc_id}-${index}`}
                className={styles.citationItem}
              >
                <p className={styles.citationMeta}>
                  {citation.doc_name ?? citation.doc_id} · {citation.org_category}
                </p>
                <label className={styles.field}>
                  <span className={styles.label}>Clause hint</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={citation.clause_hint ?? ""}
                    onChange={(event) =>
                      setRow((current) => ({
                        ...current,
                        expected_citations: updateCitationAtIndex(
                          current.expected_citations,
                          index,
                          {
                            clause_hint: event.target.value.trim() || null,
                          },
                        ),
                      }))
                    }
                    aria-label={`Clause hint for ${citation.doc_id}`}
                  />
                </label>
                <label className={styles.requiredRow}>
                  <input
                    type="checkbox"
                    checked={citation.required}
                    onChange={(event) =>
                      setRow((current) => ({
                        ...current,
                        expected_citations: updateCitationAtIndex(
                          current.expected_citations,
                          index,
                          { required: event.target.checked },
                        ),
                      }))
                    }
                    aria-label={`Required citation for ${citation.doc_id}`}
                  />
                  Required citation
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      <pre className={styles.preview} aria-label="NDJSON preview">
        {ndjsonLine}
      </pre>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          onClick={() => {
            void handleCopy();
          }}
        >
          {isCopied ? "Copied!" : "Copy NDJSON line"}
        </button>
      </div>

      {canRecordExport && (
        <form onSubmit={handleSubmitExport}>
          {errorMessage && (
            <p className={styles.error} data-testid="golden-row-export-error">
              {errorMessage}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={isSubmitting || !row.id.trim() || isGoldenIdPlaceholder}
            >
              Record export
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
